import BRAINSTATION_LOGO from '@Assets/Images/brainstation-logo.png';
import KG_LOGO from '@Assets/Images/kg-logo.png';
import ModalWrapper from '@Components/Modals/ModalWrapper/ModalWrapper';
import EmailServices from '@Services/Email.services.ts';
import { Email } from '@Types/Asset.types';
import { EditEmailFormFields, MultiDropOption } from '@Types/Form.types';
import { KGOffice } from '@Types/Office.types';
import { DROPDOWN_DEFAULT, DROPDOWN_DEFAULT_NUMBER, RECIPIENT_DROPDOWN_VALUES } from '@Utils/constants';
import { formatRequired } from '@Utils/form.utils';
import { formatLocations, formatRecipients } from '@Utils/format.utils';
import { handleRequestError } from '@Utils/request.utils';
import { VALIDATION_RULES } from '@Utils/rule.utils.ts';
import { useApolloClient } from '@apollo/client';
import { ErrorDisplay, FreeText, MultiSelectDropdown, TextArea, Toast } from '@kg-internal/pv3-uikit';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import './EditEmailModal.scss';
import { GetOfficesDocument, GetOfficesQuery } from '~/__generated__/graph';

type Props = {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  email?: Email;
};

const { EMAIL_RULES } = VALIDATION_RULES;
const block = 'email';
const EMAIL_BODY = 'emailBody';
const EMAIL_SUBJECT = 'emailSubject';
const EditEmailModal = ({ show, onClose, onSuccess, email }: Props) => {
  const client = useApolloClient();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [affectedLocations, setAffectedLocations] = useState<MultiDropOption<number>[]>([]);
  const [officesDropdown, setOfficesDropdown] = useState<MultiDropOption<number>[]>([]);
  const [recipientsMap, setRecipientsMap] = useState<Map<number, MultiDropOption<string>[]>>(new Map());
  const [offices, setOffices] = useState<KGOffice[]>([]);

  const {
    handleSubmit,
    formState: { errors },
    reset,
    register,
    setValue,
    control,
  } = useForm<EditEmailFormFields>();

  const textBodyWatch = useWatch({
    control,
    name: EMAIL_BODY,
    defaultValue: '',
  });

  const subjectWatch = useWatch({
    control,
    name: EMAIL_SUBJECT,
    defaultValue: '',
  });

  const handleEditEmail = (data: EditEmailFormFields) => {
    const mappedLocations = offices
      .filter((location) => affectedLocations.some((v) => v.value === location.OfficeID))
      .map((location) => ({
        ...location,
        Recipients: recipientsMap.get(location.OfficeID)?.map((rec) => rec.value),
      }));

    EmailServices.updateEmail({
      ...data,
      emailID: email?.id,
      emailAffectedLocations: mappedLocations,
    })
      .then(() => {
        Toast.success('Email template updated successfully.');
        setRecipientsMap(new Map());
        reset();
        onSuccess();
        onClose();
      })
      .catch(({ response }) => handleRequestError(response, setErrorMessage, true));
  };

  const getOffices = async () => {
    try {
      const { data } = await client.query<GetOfficesQuery>({
        query: GetOfficesDocument,
      });

      if (data && data.officeListV2) {
        setOffices(data.officeListV2);
        const mappedOffices: MultiDropOption<number>[] = data.officeListV2.map((office) => ({
          label: office.Name!,
          value: office.OfficeID,
        }));
        setOfficesDropdown(mappedOffices);
        setErrorMessage(undefined);
      }
    } catch (_error) {
      setErrorMessage('Error loading offices');
    }
  };

  const selectLocation = (selected: MultiDropOption<number>) => {
    setAffectedLocations((locations) => [...locations, selected]);
  };

  const removeLocation = (removed: MultiDropOption<number>) => {
    setAffectedLocations((locations) => locations.filter((location) => location.value !== removed.value));
    const newMap = new Map(recipientsMap);
    newMap.delete(removed.value);

    setRecipientsMap(new Map(newMap));
  };

  const selectRecipient = (location: number, value: MultiDropOption<string>) => {
    const foundRecipients = recipientsMap.get(location);

    if (foundRecipients) {
      setRecipientsMap(new Map(recipientsMap).set(location, [...foundRecipients, value]));
    } else {
      setRecipientsMap(new Map(recipientsMap).set(location, [value]));
    }
  };

  const removeRecipient = (location: number, value: MultiDropOption<string>) => {
    const foundRecipients = recipientsMap.get(location);

    if (foundRecipients) {
      setRecipientsMap(
        new Map(recipientsMap).set(
          location,
          foundRecipients.filter((recipient) => recipient.value !== value.value),
        ),
      );
    }
  };

  useEffect(() => {
    getOffices();
  }, []);

  useEffect(() => {
    if (email) {
      setValue(EMAIL_SUBJECT, email?.subject);
      setValue(EMAIL_BODY, email?.textBody);
      setAffectedLocations(formatLocations(email?.affectedLocations));
      setRecipientsMap(formatRecipients(email?.affectedLocations));
    }
  }, [email, show]);

  return (
    <div
      className={cx(`${block}__modal`, {
        'email__modal--no-item': affectedLocations.length === 0,
      })}>
      <ModalWrapper
        title='Update Email Template'
        primaryButtonText='Confirm'
        show={show}
        handleClose={onClose}
        primaryAction={() => handleSubmit(handleEditEmail)()}>
        {errorMessage ? (
          <ErrorDisplay inline details={errorMessage} />
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(handleEditEmail)();
            }}>
            <div className='grid'>
              <div className={`col-12 col-md-${affectedLocations.length === 0 ? '5' : '4'}`}>
                <p>
                  <b>Template Name</b> : {email?.templateName}
                </p>
                <MultiSelectDropdown<number>
                  options={officesDropdown}
                  values={affectedLocations}
                  selectOption={selectLocation}
                  removeOption={removeLocation}
                  fullWidth
                  label='Affected Locations'
                  // neccesary to reset the default value, otherwise it shows the last selected value even with an empty values array
                  value={DROPDOWN_DEFAULT_NUMBER}
                />
                <FreeText
                  label={formatRequired('Subject')}
                  fullWidth
                  error={!!errors.emailSubject}
                  errorMessage={errors.emailSubject?.message}
                  {...register(EMAIL_SUBJECT, EMAIL_RULES.SUBJECT)}
                />
                <TextArea
                  label={formatRequired('Text Body')}
                  {...register(EMAIL_BODY, EMAIL_RULES.BODY)}
                  error={!!errors.emailBody}
                  errorMessage={errors.emailBody?.message}
                />
                <br />
              </div>
              <div
                className={cx(`col-12 col-md-4 ${block}__scroll`, {
                  hidden: affectedLocations.length === 0,
                })}>
                {affectedLocations.map((location) => (
                  <div key={`${location.value}__recipients`}>
                    <p>
                      Recipients for <b>{location.label}</b>
                    </p>
                    <MultiSelectDropdown<string>
                      options={RECIPIENT_DROPDOWN_VALUES}
                      values={recipientsMap.get(location.value) || []}
                      selectOption={(selected) => selectRecipient(location.value, selected)}
                      removeOption={(removed) => removeRecipient(location.value, removed)}
                      fullWidth
                      label='Recipients'
                      // neccesary to reset the default value, otherwise it shows the last selected value even with an empty values array
                      value={DROPDOWN_DEFAULT}
                    />
                  </div>
                ))}
              </div>
              <div className={`col-12 col-md-${affectedLocations.length === 0 ? '5' : '4'}`}>
                <h3>{subjectWatch}</h3>
                <p className={`${block}__body`}>{textBodyWatch}</p>
                <div className={`${block}__logo-container`}>
                  <img src={KG_LOGO} className={`${block}__image`} alt='' />
                  <img src={BRAINSTATION_LOGO} className={`${block}__image`} alt='' />
                </div>
                <p>Konrad | BrainStation</p>
              </div>
            </div>
          </form>
        )}
      </ModalWrapper>
    </div>
  );
};

export default EditEmailModal;
